import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { ReportingTagsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { SettingsQASubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import * as ReportingTagActions from "../../../actions/reportingTags";
import { FormFilterInput } from "../../../components/form";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import {
    DEFAULT_PAGE_SIZE,
    PAGE_SIZE_OPTIONS,
} from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { IFormFilter, ITmdState } from "../../../reducers/index";
import { QALookupsLayout } from "../../index";

import "./ReportingTagsPage.scss";

interface ReportingTagsPageProps {
    formFilter: IFormFilter;
    reportingTags: Dtos.ReportingTag[];
    loadingReportingTags: boolean;
    loadReportingTagsSuccess: boolean;
    loadReportingTagsFailure: boolean;
    loadReportingTagsErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    clearReportingTags: ReportingTagActions.IReportingTagClearActionCreator;
    loadReportingTags: ReportingTagActions.IReportingTagLoadActionCreator;
    filter: ReportingTagActions.IReportingTagFilterActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
}

const ReportingTagsPage: FC<ReportingTagsPageProps> = ({
    formFilter,
    reportingTags,
    loadingReportingTags,
    loadReportingTagsSuccess,
    loadReportingTagsFailure,
    loadReportingTagsErrorCode,
    permissions,
    pageSize,
    clearReportingTags,
    loadReportingTags,
    filter,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize
}) => {
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showReportingTags = !loadingReportingTags && loadReportingTagsSuccess;


    // Load reporting tags
    useEffect(() => {
        if (!initalised) {
            setDrawerItems(SettingsQASubMenu(permissions));
            setBreadcrumbs(ReportingTagsBreadcrumbs());

            loadReportingTags();

            setInitalised(true);
        }
    }, [
        initalised,
        setDrawerItems,
        SettingsQASubMenu,
        permissions,
        setBreadcrumbs,
        ReportingTagsBreadcrumbs,
        loadReportingTags,
        setInitalised,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadReportingTagsFailure) {
            navigate(`/error/${loadReportingTagsErrorCode}`);
        }
    }, [loadReportingTagsFailure, loadReportingTagsErrorCode, navigate]);

    // Cleanup effect to clear the reporting tags on component unmount
    useEffect(() => {
        return () => {
            clearReportingTags();
        };
    }, [clearReportingTags]);

    const createTable = useMemo(() => {
        if (!reportingTags || reportingTags.length == 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no reporting tags to view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = reportingTags.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={reportingTags}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : reportingTags.length
                    }
                    pageSize={showPagination ? pageSize : reportingTags.length}
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no reporting tags to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string =
                                    "/settings/qa/reportingtags/" +
                                    rowInfo.original.id;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Light.LOOKUP}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "value",
                            Header: "Name",
                            accessor: "value",
                        },
                    ]}
                />
            </div>
        );
    }, [
        reportingTags,
        PAGE_SIZE_OPTIONS,
        pageSize,
        DEFAULT_PAGE_SIZE,
        navigate,
        setPageSize,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Reporting Tags</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />

                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.ReportingTagsCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={"/settings/qa/reportingtags/create"}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showReportingTags ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showReportingTags ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [formFilter, filter, permissions, showReportingTags, createTable]);

    return (
        <QALookupsLayout
            loading={loadingReportingTags}
            permission={Dtos.Permission.ReportingTagsView}
        >
            {renderContent}
        </QALookupsLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<ReportingTagsPageProps> => {
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        formFilter: state.reportingTags.formFilter,
        reportingTags: state.reportingTags.formFilteredData,
        loadingReportingTags:
            state.reportingTags.loadState &&
            state.reportingTags.loadState.status == RequestState.Pending,
        loadReportingTagsSuccess:
            state.reportingTags.loadState &&
            state.reportingTags.loadState.status == RequestState.Success,
        loadReportingTagsFailure:
            state.reportingTags.loadState &&
            state.reportingTags.loadState.status == RequestState.Failure,
        loadReportingTagsErrorCode: state.reportingTags.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
    };
};

const mapDispatchToProps = (dispatch): Partial<ReportingTagsPageProps> => {
    return {
        loadReportingTags: bindActionCreators(
            ReportingTagActions.LoadReportingTag,
            dispatch
        ),
        clearReportingTags: bindActionCreators(
            ReportingTagActions.Clear,
            dispatch
        ),
        filter: bindActionCreators(ReportingTagActions.Filter, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        )
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ReportingTagsPage);
